// src/pages/Users/Techniques/utils/uv2TrainingUtils.js

import {
  UV2_DEFENSE_ATTACKS,
  UV2_GRADE_CONFIG,
  UV2_PROGRAM_BY_GRADE,
} from "../data/uv2ProgramData";

import { strip } from "./techniquesUtils";

export function normalizeUv2TechniqueName(
  value = "",
) {
  if (!value) {
    return "";
  }

  const withoutParentheses =
    String(value).replace(
      /\([^)]*\)/g,
      " ",
    );

  return strip(withoutParentheses)
    .replace(/['’]/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function compactName(value = "") {
  return normalizeUv2TechniqueName(
    value,
  ).replace(/-/g, "");
}

export function buildTechniqueLookup(
  allTechniques = [],
) {
  const lookup = new Map();

  const safeTechniques =
    Array.isArray(allTechniques)
      ? allTechniques.filter(Boolean)
      : [];

  safeTechniques.forEach(
    (technique) => {
      const possibleNames = [
        technique?.nom,
        technique?.nom_japonais,
        technique?.name,
        technique?.japanese_name,
      ].filter(Boolean);

      possibleNames.forEach((name) => {
        const normalized =
          normalizeUv2TechniqueName(name);

        const compact =
          compactName(name);

        if (
          normalized &&
          !lookup.has(normalized)
        ) {
          lookup.set(
            normalized,
            technique,
          );
        }

        if (
          compact &&
          !lookup.has(compact)
        ) {
          lookup.set(
            compact,
            technique,
          );
        }
      });
    },
  );

  return lookup;
}

export function resolveTechniqueLink(
  name,
  lookup,
) {
  if (!name || !lookup) {
    return null;
  }

  const normalized =
    normalizeUv2TechniqueName(name);

  const compact =
    compactName(name);

  return (
    lookup.get(normalized) ||
    lookup.get(compact) ||
    null
  );
}

function shuffle(items = []) {
  const result = [...items];

  for (
    let index = result.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1),
    );

    [
      result[index],
      result[randomIndex],
    ] = [
      result[randomIndex],
      result[index],
    ];
  }

  return result;
}

function randomItem(items = []) {
  if (!items.length) {
    return null;
  }

  return items[
    Math.floor(
      Math.random() * items.length,
    )
  ];
}

function pickAcrossFamilies(
  section = {},
  count = 1,
  domain = "",
) {
  const entries = Object.entries(
    section,
  ).filter(
    ([, names]) =>
      Array.isArray(names) &&
      names.length > 0,
  );

  const picked = [];
  const usedNames = new Set();

  shuffle(entries).forEach(
    ([family, names]) => {
      if (picked.length >= count) {
        return;
      }

      const availableNames =
        names.filter(
          (name) =>
            !usedNames.has(
              normalizeUv2TechniqueName(
                name,
              ),
            ),
        );

      const selected =
        randomItem(availableNames);

      if (!selected) {
        return;
      }

      usedNames.add(
        normalizeUv2TechniqueName(
          selected,
        ),
      );

      picked.push({
        id: `${domain}-${family}-${selected}-${picked.length}`,
        domain,
        family,
        name: selected,
      });
    },
  );

  const remainingItems = shuffle(
    entries.flatMap(
      ([family, names]) =>
        names
          .filter(
            (name) =>
              !usedNames.has(
                normalizeUv2TechniqueName(
                  name,
                ),
              ),
          )
          .map((name) => ({
            id: `${domain}-${family}-${name}`,
            domain,
            family,
            name,
          })),
    ),
  );

  while (
    picked.length < count &&
    remainingItems.length > 0
  ) {
    const selected =
      remainingItems.shift();

    if (!selected) {
      break;
    }

    const normalizedName =
      normalizeUv2TechniqueName(
        selected.name,
      );

    if (
      usedNames.has(normalizedName)
    ) {
      continue;
    }

    usedNames.add(normalizedName);

    picked.push({
      ...selected,
      id: `${selected.id}-${picked.length}`,
    });
  }

  return picked;
}

function pickDefense(
  series = [],
  count = 2,
) {
  const usedNames = new Set();

  const pool = shuffle(
    series.flatMap((serie) =>
      (
        UV2_DEFENSE_ATTACKS[
          serie
        ] || []
      ).map((name) => ({
        id: `defense-${serie}-${name}`,
        domain: "Défense",
        family: `Série ${serie}`,
        serie,
        name,
      })),
    ),
  ).filter((item) => {
    const normalizedName =
      normalizeUv2TechniqueName(
        item.name,
      );

    if (
      usedNames.has(normalizedName)
    ) {
      return false;
    }

    usedNames.add(normalizedName);

    return true;
  });

  return pool.slice(0, count);
}

export function generateUv2Draw(
  gradeKey = "1dan",
) {
  const gradeConfig =
    UV2_GRADE_CONFIG.find(
      (item) =>
        item.key === gradeKey,
    ) || UV2_GRADE_CONFIG[0];

  const program =
    UV2_PROGRAM_BY_GRADE[
      gradeConfig.key
    ];

  if (!program) {
    return null;
  }

  return {
    id: `uv2-${gradeConfig.key}-${Date.now()}`,
    generatedAt:
      new Date().toISOString(),
    gradeKey: gradeConfig.key,

    nage: pickAcrossFamilies(
      program.nage,
      gradeConfig.draw.nage,
      "Nage-waza",
    ),

    newaza: pickAcrossFamilies(
      program.newaza,
      gradeConfig.draw.newaza,
      "Ne-waza",
    ),

    defense: pickDefense(
      gradeConfig.defenseSeries,
      gradeConfig.draw.defense,
    ),
  };
}

export function countProgramItems(
  section = {},
) {
  return Object.values(
    section,
  ).reduce((total, names) => {
    if (!Array.isArray(names)) {
      return total;
    }

    return total + names.length;
  }, 0);
}